import React from 'react';
import { toast } from 'sonner';
import { ChevronDown, Pencil, Trash2, Plus } from 'lucide-react';
import { adminApi } from '../../lib/adminAuth';

const CATS = { festival: 'Festival', holiday: 'Holiday', office: 'Office notice' };
const inputCls = 'w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white';

function Editor({ t, onDone }) {
  const [f, setF] = React.useState(t);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (f.id) await adminApi.put(`/api/admin/push/templates/${f.id}`, f); else await adminApi.post('/api/admin/push/templates', f);
      toast.success('Template saved'); onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className="p-3 rounded-xl border border-slate-300 bg-slate-50 space-y-2" data-testid="push-template-editor">
      <div className="flex gap-2">
        <input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Template name" className={inputCls} data-testid="push-template-name-input" />
        <select value={f.category} onChange={(e) => set('category', e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white" data-testid="push-template-category-select">
          {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="Title" className={inputCls} data-testid="push-template-title-input" />
      <textarea value={f.body} onChange={(e) => set('body', e.target.value)} rows={2} placeholder="Message" className={`${inputCls} h-auto py-2`} data-testid="push-template-body-input" />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => onDone(false)} className="h-8 px-3 text-sm text-slate-500" data-testid="push-template-cancel">Cancel</button>
        <button type="button" onClick={save} className="h-8 px-4 rounded-full bg-[#1a1a2e] text-white text-sm font-semibold" data-testid="push-template-save">Save</button>
      </div>
    </div>
  );
}

export default function TemplatesPanel({ templates, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await adminApi.delete(`/api/admin/push/templates/${t.id}`); toast.success('Deleted'); onChange(); } catch { toast.error('Could not delete'); }
  };
  const done = (changed) => { setEditing(null); if (changed) onChange(); };
  return (
    <div className="soft-card p-5" data-testid="push-templates-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="push-templates-toggle">
        <span className="flex-1 font-semibold text-[#1a1a2e]">Message templates</span>
        <span className="text-xs text-slate-500" data-testid="push-templates-count">{templates.length}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {editing && !editing.id && <Editor t={editing} onDone={done} />}
          {!editing && <button type="button" onClick={() => setEditing({ name: '', title: '', body: '', category: 'festival' })} className="h-8 px-3 rounded-full border border-slate-200 text-sm inline-flex items-center gap-1 hover:bg-slate-50" data-testid="push-template-add"><Plus className="w-4 h-4" /> New template</button>}
          {templates.map((t) => (editing?.id === t.id ? <Editor key={t.id} t={editing} onDone={done} /> : (
            <div key={t.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid={`push-template-${t.id}`}>
              <span className="text-[10px] uppercase tracking-wide text-slate-400 w-20 pt-0.5">{CATS[t.category]}</span>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900">{t.name}</p><p className="text-xs text-slate-500 truncate">{t.title} — {t.body}</p></div>
              <button type="button" onClick={() => setEditing(t)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`push-template-edit-${t.id}`}><Pencil className="w-3.5 h-3.5 text-slate-500" /></button>
              <button type="button" onClick={() => remove(t)} className="p-1.5 rounded hover:bg-rose-50" data-testid={`push-template-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
            </div>
          )))}
        </div>
      )}
    </div>
  );
}
